import * as RadixPopover from "@radix-ui/react-popover";
import { forwardRef, useMemo, useRef, useState, type KeyboardEvent } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "@/components/ui/overlays/overlayPositioning";
import { Chip } from "@/components/primitives/Chip";

export type TagInputProps = {
  /** Current tags (controlled). */
  value: string[];
  /** Fired with the updated list on add / remove. */
  onChange: (value: string[]) => void;
  /** Optional existing-tag suggestions for autocomplete (filtered as you type). */
  suggestions?: string[];
  /** Allow committing values outside `suggestions` (default true). */
  creatable?: boolean;
  placeholder?: string;
  /** Max suggestions shown in the dropdown (default 8). */
  maxSuggestions?: number;
  size?: "sm" | "md";
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
};

type TagInputSize = "sm" | "md";

interface TagInputRootOwnerState {
  size: TagInputSize;
  error: boolean;
  disabled: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build, except text-entry focus boxes now render a single
 * active border without the old ring/outline layer. */

const tiRootBase: CSSObject = {
  display: "flex",
  width: "100%",
  cursor: "text",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".375rem",
  borderRadius: "var(--bfrs-input-radius)",
  borderWidth: "1px",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  // `bfrs-duration-150` after `bfrs-transition` re-declares the same .15s.
  transitionDuration: ".15s"
};

const tiRootSize: Record<TagInputSize, CSSObject> = {
  sm: { minHeight: "1.75rem", paddingTop: ".25rem", paddingBottom: ".25rem" },
  md: { minHeight: "2.25rem", paddingTop: ".375rem", paddingBottom: ".375rem" }
};

const tiRootError: CSSObject = {
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))"
  }
};

const tiRootDefaultBorder = (size: TagInputSize): CSSObject => ({
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "&:focus-within": {
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
  },
  ...(size === "sm"
    ? {
        "&[data-filled]": {
          borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))"
        }
      }
    : null)
});

const tiRootDisabled: CSSObject = {
  cursor: "not-allowed",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))",
  opacity: ".7"
};

const TagInputRoot = styled("div", {
  label: "AgenticTagInput-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: TagInputRootOwnerState }>(({ ownerState }) => [
  tiRootBase,
  tiRootSize[ownerState.size],
  ownerState.error ? tiRootError : tiRootDefaultBorder(ownerState.size),
  ownerState.disabled && tiRootDisabled
]);

const TagInputField = styled("input", {
  label: "AgenticTagInput-field"
})({
  minWidth: "80px",
  flex: "1 1 0%",
  borderStyle: "none",
  backgroundColor: "transparent",
  fontSize: "12px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "none",
  "&::-moz-placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&::placeholder": {
    "--tw-text-opacity": "1",
    color: "rgb(var(--bfrs-color-text-placeholder) / var(--tw-text-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
});

const TagInputContent = styled(RadixPopover.Content, {
  label: "AgenticTagInput-content"
})({
  zIndex: 1000,
  // `bfrs-w-[var(--radix-popover-anchor-width)]` was dropped by tailwind-merge:
  // the later searchableSelectContentCollisionClassName carries
  // `bfrs-w-[var(--radix-popover-trigger-width)]` (same w-* group), so the
  // trigger-width value below is the effective one.
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: ".25rem",
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  // searchableSelectContentCollisionClassName, inlined.
  maxHeight: "min(18rem,var(--radix-popover-content-available-height))",
  width: "var(--radix-popover-trigger-width)",
  overflow: "hidden"
});

// searchableSelectListboxCollisionClassName, inlined.
const TagInputListbox = styled("div", {
  label: "AgenticTagInput-listbox"
})({
  maxHeight: "min(14rem,max(4rem,calc(var(--radix-popover-content-available-height) - 3rem)))",
  overflow: "auto"
});

interface TagInputOptionOwnerState {
  size: TagInputSize;
  active: boolean;
}

const tiOptionSize: Record<TagInputSize, CSSObject> = {
  sm: { height: "1.75rem", paddingLeft: ".625rem", paddingRight: ".625rem" },
  md: { height: "2.25rem", paddingLeft: ".75rem", paddingRight: ".75rem" }
};

const TagInputOption = styled("button", {
  label: "AgenticTagInput-option",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: TagInputOptionOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    width: "100%",
    cursor: "pointer",
    alignItems: "center",
    borderRadius: "var(--bfrs-radius-sm)",
    textAlign: "left",
    fontSize: "12px",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  tiOptionSize[ownerState.size],
  ownerState.active && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
  }
]);

export const TagInput = forwardRef<HTMLDivElement, TagInputProps>(
  (
    {
      value,
      onChange,
      suggestions = [],
      creatable = true,
      placeholder = "Add tag",
      maxSuggestions = 8,
      size = "md",
      disabled,
      error,
      className,
      id,
      "aria-describedby": describedBy,
      "aria-invalid": ariaInvalid
    },
    ref
  ) => {
    const [input, setInput] = useState("");
    const [focused, setFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const q = input.trim().toLowerCase();
    const matches = useMemo(
      () =>
        q
          ? suggestions
              .filter((s) => s.toLowerCase().includes(q) && !value.includes(s))
              .slice(0, maxSuggestions)
          : [],
      [q, suggestions, value, maxSuggestions]
    );

    const open = focused && matches.length > 0;

    const addTag = (raw: string) => {
      const tag = raw.trim();
      if (tag && !value.includes(tag)) onChange([...value, tag]);
      setInput("");
      setActiveIndex(0);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        if (open && matches[activeIndex]) {
          addTag(matches[activeIndex]);
        } else if (creatable) {
          addTag(input);
        }
      } else if (e.key === "Backspace" && !input && value.length) {
        onChange(value.slice(0, -1));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Escape") {
        setFocused(false);
        inputRef.current?.blur();
      }
    };

    const hasTags = value.length > 0;

    return (
      <RadixPopover.Root open={open} onOpenChange={(next) => { if (!next) setFocused(false); }}>
        <RadixPopover.Anchor asChild>
          <TagInputRoot
            ref={ref}
            data-filled={hasTags && !error ? "" : undefined}
            aria-describedby={describedBy}
            aria-invalid={ariaInvalid ?? error}
            ownerState={{ size, error: Boolean(error), disabled: Boolean(disabled) }}
            className={className}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            {value.map((tag) => (
              <Chip
                key={tag}
                size={size === "sm" ? "xs" : "sm"}
                tone="primary"
                variant="soft"
                removable={!disabled}
                removeLabel={`Remove ${tag}`}
                onRemove={disabled ? undefined : () => onChange(value.filter((t) => t !== tag))}
              >
                {tag}
              </Chip>
            ))}
            <TagInputField
              ref={inputRef}
              id={id}
              value={input}
              disabled={disabled}
              placeholder={hasTags ? undefined : placeholder}
              aria-autocomplete="list"
              aria-expanded={open}
              onChange={(e) => {
                setInput(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
          </TagInputRoot>
        </RadixPopover.Anchor>
        <RadixPopover.Portal>
          <BfrsPortalScope>
            <TagInputContent
              align="start"
              sideOffset={6}
              collisionPadding={floatingCollisionPadding}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <TagInputListbox role="listbox">
                {matches.map((s, index) => (
                  <TagInputOption
                    key={s}
                    type="button"
                    role="option"
                    aria-selected={activeIndex === index}
                    ownerState={{ size, active: activeIndex === index }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addTag(s);
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    {s}
                  </TagInputOption>
                ))}
              </TagInputListbox>
            </TagInputContent>
          </BfrsPortalScope>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  }
);

TagInput.displayName = "TagInput";

export const TagsInput = TagInput;
export type TagsInputProps = TagInputProps;
